import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Apollo, gql } from 'apollo-angular';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { combineLatest, delay, distinctUntilChanged, map } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { NavigationService } from '~ngx-shared/services';
import { DialogUtil } from '~ngx-shared/utils/dialog.util';
import { EducationUserAgreementComponent } from '~shared/education/components/education-user-agreement/education-user-agreement.component';
import { UserService } from '~shared/services';

@Injectable({
  providedIn: 'root'
})
export class EducationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly apollo = inject(Apollo);
  private readonly userService = inject(UserService);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly navigationService = inject(NavigationService);
  private readonly authorizationService = inject(AuthorizationService);

  init(): void {
    combineLatest([
      this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()),
      this.authorizationService.currentRole$.pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged()
      )
    ])
      .pipe(takeUntilDestroyed(this.destroyRef), delay(2000))
      .subscribe(([user, role]) => {
        if (role && user?.person_id) {
          this.apollo
            .query<{ result: { id?: number; user_agreement_accepted?: boolean }[] }>({
              query: gql`
                query GetEducationUserAgreementByPersonId($personId: bigint!) {
                  result: education_latest_${role}_data(where: { person_id: { _eq: $personId } }) {
                    id
                    user_agreement_accepted
                  }
                }
              `,
              variables: {
                personId: user?.person_id
              }
            })
            .pipe(map(queryResult => queryResult.data?.result?.[0]))
            .subscribe(result => {
              if (result) {
                if (!result.user_agreement_accepted) {
                  this.dialogService
                    .open(EducationUserAgreementComponent, {
                      ...DialogUtil.BASE_DIALOG_CONFIG,
                      header: this.translocoService.translate('shared.user_agreement'),
                      closable: false,
                      data: {
                        id: result.id,
                        role
                      }
                    })
                    ?.onClose.pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(result => {
                      if (result && result.output) {
                        this.navigationService.navigateTo(['/', 'profile']);
                      }
                    });
                }
              }
            });
        }
      });
  }
}
